import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

import { FOCUS_RING_CLASS } from './focus-ring'

/**
 * Toast region for non-blocking status messages.
 *
 * Per `doc/design-system.md` / the MUR "Design Tokens and Accessibility" page (announcements):
 *   - `role=status` + `aria-live=polite` so SR users hear sign-in / sign-out
 *     and other non-critical events.
 *   - Auto-dismiss after 5 seconds; manual dismiss via the Close button.
 *   - At M1 the only producers are the auth flows (sign-in landed,
 *     sign-out completed). The reveal-audit toast in the MUR "Mockups" page
 *     reuses the same primitive when PR-E2 lands.
 */

export interface Toast {
  readonly id: string
  readonly message: string
  /** `status` (default) = polite; `alert` = assertive (use sparingly). */
  readonly kind?: 'status' | 'alert'
}

interface ToastContextValue {
  readonly toasts: readonly Toast[]
  readonly show: (toast: Omit<Toast, 'id'>) => void
  readonly dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<readonly Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id))
  }, [])

  const show = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`
      setToasts((current) => [...current, { ...toast, id }])
      // Auto-dismiss after 5 seconds.
      setTimeout(() => dismiss(id), 5000)
    },
    [dismiss],
  )

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, show, dismiss }),
    [toasts, show, dismiss],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastRegion />
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast() must be used inside <ToastProvider>')
  }
  return ctx
}

/**
 * Variant of `useToast()` that silently no-ops when no provider is mounted.
 * For components that may be rendered in isolation tests or in contexts
 * where a toast emission is a nice-to-have, not a contract.
 */
export function useOptionalToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (ctx) return ctx
  return NO_OP_TOAST
}

const NO_OP_TOAST: ToastContextValue = {
  toasts: [],
  show: () => undefined,
  dismiss: () => undefined,
}

/** The live region that paints toasts. Always present in the DOM so SR
 *  users have a stable announcement target. */
function ToastRegion(): JSX.Element {
  const ctx = useContext(ToastContext)
  const toasts = ctx?.toasts ?? []

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-murmuration-5 right-murmuration-5 z-50 flex flex-col gap-murmuration-2"
    >
      {toasts.map((t) => (
        // No role on the toast itself: the wrapper is already an
        // aria-live region, and a role=status/alert inside it is a nested
        // live region, which can announce the same message twice.
        <div
          key={t.id}
          className="pointer-events-auto flex min-w-[240px] items-start gap-murmuration-3 rounded-md bg-bg-surface px-murmuration-4 py-murmuration-3 text-murmuration-body text-text-primary shadow-elev-2 ring-1 ring-border-default"
        >
          <span className="flex-1">{t.message}</span>
          <button
            type="button"
            aria-label="Dismiss notification"
            onClick={() => ctx?.dismiss(t.id)}
            className={`-mr-murmuration-1 flex-none rounded-sm px-murmuration-1 text-text-muted hover:text-text-primary ${FOCUS_RING_CLASS}`}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path
                d="M1 1l10 10M11 1L1 11"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      ))}
    </div>
  )
}
